/**
 * Calendar-day helpers for the organisation timezone.
 * Kept aligned with backend/src/utils/timezone.utils.ts.
 * Date-only values stored as UTC midnight keep that calendar date.
 */

import moment from 'moment'

/** Used when an organisation has not chosen a timezone. Matches the previous platform default. */
export const DEFAULT_ORGANISATION_TIMEZONE = 'Australia/Sydney'

const DAY_MS = 86_400_000

type ZonedParts = {
  year: number
  month: number
  day: number
  hour: number
  minute: number
  second: number
}

export function isValidIanaTimeZone(timeZone: string): boolean {
  const trimmed = timeZone.trim()
  if (!trimmed) return false
  try {
    Intl.DateTimeFormat('en-US', { timeZone: trimmed })
    return true
  } catch {
    return false
  }
}

export function resolveTimeZone(timeZone: string | null | undefined): string {
  if (timeZone && isValidIanaTimeZone(timeZone)) return timeZone.trim()
  return DEFAULT_ORGANISATION_TIMEZONE
}

export function zonedParts(date: Date, timeZone: string): ZonedParts {
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: resolveTimeZone(timeZone),
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  })

  const bag: Record<string, string> = {}
  for (const part of formatter.formatToParts(date)) {
    if (part.type !== 'literal') bag[part.type] = part.value
  }

  let hour = Number(bag.hour)
  if (hour === 24) hour = 0

  return {
    year: Number(bag.year),
    month: Number(bag.month),
    day: Number(bag.day),
    hour,
    minute: Number(bag.minute),
    second: Number(bag.second),
  }
}

export function yearMonthDayInTimeZone(date: Date, timeZone: string): string {
  const parts = zonedParts(date, timeZone)
  return `${parts.year}-${pad(parts.month)}-${pad(parts.day)}`
}

function isUtcDateOnly(date: Date): boolean {
  return (
    date.getUTCHours() === 0 &&
    date.getUTCMinutes() === 0 &&
    date.getUTCSeconds() === 0 &&
    date.getUTCMilliseconds() === 0
  )
}

export function calendarDayKey(date: Date, timeZone: string): string {
  if (isUtcDateOnly(date)) return date.toISOString().slice(0, 10)
  return yearMonthDayInTimeZone(date, timeZone)
}

export function calendarDaysBetween(fromYmd: string, toYmd: string): number {
  const [fromYear, fromMonth, fromDay] = fromYmd.split('-').map(Number)
  const [toYear, toMonth, toDay] = toYmd.split('-').map(Number)
  const from = Date.UTC(fromYear, fromMonth - 1, fromDay)
  const to = Date.UTC(toYear, toMonth - 1, toDay)
  return Math.round((to - from) / DAY_MS)
}

export function daysUntilInTimeZone(target: Date, timeZone: string, now = new Date()): number {
  return calendarDaysBetween(yearMonthDayInTimeZone(now, timeZone), calendarDayKey(target, timeZone))
}

export function formatInTimeZone(date: Date, timeZone: string, format: string): string {
  if (isUtcDateOnly(date)) return moment.utc(date).format(format)

  const parts = zonedParts(date, timeZone)
  const stamp = `${parts.year}-${pad(parts.month)}-${pad(parts.day)} ${pad(parts.hour)}:${pad(parts.minute)}:${pad(parts.second)}`
  return moment.utc(stamp, 'YYYY-MM-DD HH:mm:ss', true).format(format)
}

/** Display a stored YYYY-MM-DD without shifting it into another timezone. */
export function formatDateOnly(value: string, locale = 'en-AU'): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value)
  if (!match) return value
  const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])))
  return new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  }).format(date)
}

export function localYearMonthDay(date: Date): string {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
}

export function timeZoneOffsetLabel(timeZone: string, now = new Date()): string {
  const zone = resolveTimeZone(timeZone)
  const name = new Intl.DateTimeFormat('en-US', { timeZone: zone, timeZoneName: 'shortOffset' })
    .formatToParts(now)
    .find((part) => part.type === 'timeZoneName')?.value
  return name || zone
}

function pad(value: number): string {
  return String(value).padStart(2, '0')
}
